import { Landmark, Plus } from "lucide-react";
import { cn } from "@/utils/Functions";
import { Button } from "@/components/ui/Button";
import { Card, CardHeader, CardTitle, CardPanel } from "@/components/ui/card";
import {
  Empty,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  EmptyDescription,
} from "@/components/ui/empty";
import type { ConnectedAccount } from "@/types/portfolio.types";

type Props = {
  accounts: ConnectedAccount[];
  entityName: string;
};

const typeLabels: Record<string, string> = {
  checking: "Checking",
  savings: "Savings",
  card: "Card",
  escrow: "Escrow",
};

export function ConnectedAccounts({ accounts, entityName }: Props) {
  if (accounts.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Connected Accounts</CardTitle>
        </CardHeader>
        <CardPanel>
        <Empty className="border-0 p-4">
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <Landmark className="size-4.5" />
            </EmptyMedia>
            <EmptyTitle>No Connected Accounts</EmptyTitle>
            <EmptyDescription>
              Connect a bank account or payment method to enable automated payments and real-time balance tracking.
            </EmptyDescription>
          </EmptyHeader>
          <div className="flex gap-2 mt-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => {/* demo placeholder */}}
            >
              Connect with Stripe
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {/* demo placeholder */}}
            >
              Connect Bank (Plaid)
            </Button>
          </div>
        </Empty>
        </CardPanel>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">Connected Accounts</CardTitle>
      </CardHeader>
      <CardPanel>
      <div className="space-y-2">
        {accounts.map((acct) => (
          <div
            key={acct.id}
            className="flex items-center gap-3 p-3 rounded-xl border border-border/50 hover:bg-muted/30 transition-colors"
          >
            <div className={cn(
              "size-9 rounded-full grid place-items-center shrink-0",
              acct.provider === "stripe" ? "bg-violet-500/10 text-violet-500" : "bg-blue-500/10 text-blue-500"
            )}>
              {acct.provider === "stripe" ? (
                <span className="text-sm font-bold">S</span>
              ) : (
                <Landmark className="size-4" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-foreground">{acct.institution}</div>
              <div className="text-xs text-muted-foreground">{acct.label} · ····{acct.lastFour}</div>
            </div>
            <span className="text-[10px] font-medium px-2 py-0.5 rounded-full text-muted-foreground bg-muted/50 capitalize">
              {typeLabels[acct.type] ?? acct.type}
            </span>
            <span className="flex items-center gap-1 text-xs text-muted-foreground">
              <span className="size-1.5 rounded-full bg-emerald-500" />
              {acct.status}
            </span>
          </div>
        ))}
      </div>
      <Button size="sm" variant="outline" className="w-full mt-3">
        <Plus className="size-3.5 mr-1" />
        Add Account
      </Button>
      </CardPanel>
    </Card>
  );
}
